/**
 * AttributionTogglesSection — one Switch per network (Facebook · TikTok · UTM/Google).
 * State is DERIVED from the config arrays (DC-20); ON/OFF rewrite urlParams + serverData +
 * stripeMappings through `applyToggle`. Rows a rule depends on are kept and explained (AC-051).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Box, FormControlLabel, Switch, Typography, useTheme } from '@mui/material';
import { WP } from '@test-kit/selectors/wp-dialog';
import { prefersReducedMotion, pulseGuideTarget } from '@modules/guides';
import type { EntryScreenRule, VariableBinding } from '@modules/webplacements/types/webplacements.types';
import {
  ATTRIBUTION_NETWORK_LABELS,
  ATTRIBUTION_PRESET_KEYS,
  AttributionBudgetExceededError,
  applyToggle,
  deriveToggleState,
  explainProtectedRows,
  type AttributionConfigSlice,
  type AttributionPresetKey,
} from '@modules/webplacements/config/attributionPresets';

export interface AttributionTogglesSectionProps {
  config: AttributionConfigSlice;
  protectedKeys: Set<string>;
  entryScreenRules?: EntryScreenRule[];
  variableBindings?: VariableBinding[];
  onChange: (next: AttributionConfigSlice, key: AttributionPresetKey, on: boolean) => void;
  disabled?: boolean;
  /** Publish-gate "← Step 2 · toggle" lands here: scroll + pulse on mount (AC-026). */
  highlight?: boolean;
  onBudgetExceeded?: (message: string) => void;
}

const TOGGLE_TEST_ID: Record<AttributionPresetKey, string> = {
  facebook: WP.attributionToggleFacebook,
  tiktok: WP.attributionToggleTiktok,
  utm: WP.attributionToggleUtm,
};

const WRITES: Record<AttributionPresetKey, string> = {
  facebook: 'fbclid + utm_* in URL parameters · client_ip, client_user_agent, facebook.fbp, facebook.fbc in Server Data and Stripe metadata',
  tiktok: 'ttclid + utm_* in URL parameters · client_ip, client_user_agent, tiktok.ttp, tiktok.ttclid in Server Data and Stripe metadata',
  utm: 'utm_source, utm_medium, utm_campaign, utm_content, utm_term, gclid in URL parameters',
};

export const AttributionTogglesSection: React.FC<AttributionTogglesSectionProps> = ({
  config, protectedKeys, entryScreenRules, variableBindings, onChange, disabled = false, highlight = false, onBudgetExceeded,
}) => {
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const state = useMemo(() => deriveToggleState(config), [config]);

  useEffect(() => {
    if (!highlight || !rootRef.current) return undefined;
    const reduced = prefersReducedMotion();
    rootRef.current.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    return pulseGuideTarget(rootRef.current, theme.palette.primary.main, reduced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, theme.palette.primary.main]);

  const handleToggle = (key: AttributionPresetKey, on: boolean) => {
    try {
      onChange(applyToggle(config, key, on, protectedKeys), key, on);
    } catch (error) {
      if (error instanceof AttributionBudgetExceededError) {
        onBudgetExceeded?.(error.message);
        return;
      }
      throw error;
    }
  };

  return (
    <Box ref={rootRef} data-testid={WP.attributionSection} sx={{ mb: 2, p: theme.spacing(1.5, 2), border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Attribution</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Each toggle writes the network&apos;s parameters into the sources below and maps them to Stripe metadata. State is read back from the rows, so hand-added rows count.
      </Typography>
      {ATTRIBUTION_PRESET_KEYS.map((key) => {
        const kept = state[key] ? explainProtectedRows({ ...config, entryScreenRules, variableBindings }, key) : [];
        return (
          <Box key={key} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={state[key]}
                  disabled={disabled}
                  onChange={(_event, checked) => handleToggle(key, checked)}
                  // Switch hard-codes slotProps.input = { role: 'switch' } and spreads it
                  // AFTER the deprecated `inputProps` prop, so `inputProps` alone is
                  // silently dropped; `slotProps.input` is the only path that reaches
                  // the actual <input>. Keep `role: 'switch'` so we don't regress a11y.
                  slotProps={{
                    input: {
                      role: 'switch',
                      'data-testid': TOGGLE_TEST_ID[key],
                      'aria-label': `${ATTRIBUTION_NETWORK_LABELS[key]} attribution`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">{ATTRIBUTION_NETWORK_LABELS[key]}</Typography>
                  <Typography variant="caption" color="text.secondary">{WRITES[key]}</Typography>
                </Box>
              )}
            />
            {kept.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 6 }}>
                Turning off keeps: {kept.map((k) => `${k.key} (${k.reason === 'entry-rule' ? 'used by an entry-screen rule' : k.reason === 'variable-binding' ? 'used by a variable binding' : 'added by hand'})`).join(', ')}.
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default AttributionTogglesSection;
